import styled from 'styled-components';
import { Button, ChromeButton, DangerButton, Input } from './ui';
import type { ThemeMode } from '../types';

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    linear-gradient(135deg, ${p => p.theme.chrome} 0%, ${p => p.theme.chrome} 46%, ${p => p.theme.bg} 46%, ${p => p.theme.bg} 100%);
`;

const Card = styled.form`
  width: min(460px, 100%);
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
  box-shadow: ${p => p.theme.shadow};
  padding: 36px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: ${p => p.theme.text};
  font-family: 'Raleway', system-ui, sans-serif;
  font-size: clamp(32px, 6vw, 44px);
  font-weight: 700;
  letter-spacing: -0.035em;
`;

const Hint = styled.p`
  margin: 0 0 22px;
  color: ${p => p.theme.muted};
  line-height: 1.5;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 14px;
  color: ${p => p.theme.text};
  font-size: 14px;
  font-weight: 700;
`;

const FieldText = styled.span`
  display: block;
  margin-bottom: 7px;
`;

const ErrorBox = styled.div`
  margin-bottom: 14px;
  padding: 12px 14px;
  color: ${p => p.theme.dangerStrong};
  background: ${p => p.theme.dangerSoft};
  border: 1px solid ${p => p.theme.danger};
  border-radius: 8px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

type CommonProps = {
  themeMode: ThemeMode;
  onToggleTheme: () => void;
};

export function LoadingScreen({ themeMode, onToggleTheme }: CommonProps) {
  return (
    <Shell>
      <Card as="div">
        <Title>BlanketChat</Title>
        <Hint>Checking your secure PocketBase session...</Hint>
        <ChromeButton type="button" onClick={onToggleTheme}>{themeMode === 'light' ? 'Dark mode' : 'Light mode'}</ChromeButton>
      </Card>
    </Shell>
  );
}

type LoginProps = CommonProps & {
  identity: string;
  password: string;
  error: string;
  isLoading: boolean;
  onIdentityChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

export function LoginScreen({
  themeMode,
  onToggleTheme,
  identity,
  password,
  error,
  isLoading,
  onIdentityChange,
  onPasswordChange,
  onSubmit,
}: LoginProps) {
  return (
    <Shell>
      <Card onSubmit={onSubmit}>
        <Title>BlanketChat</Title>
        <Hint>Accesso riservato con PocketBase. Niente registrazione, solo utenti abilitati.</Hint>
        {error && <ErrorBox>{error}</ErrorBox>}
        <Field>
          <FieldText>Email or username</FieldText>
          <Input value={identity} onChange={event => onIdentityChange(event.target.value)} autoComplete="username" required />
        </Field>
        <Field>
          <FieldText>Password</FieldText>
          <Input type="password" value={password} onChange={event => onPasswordChange(event.target.value)} autoComplete="current-password" required />
        </Field>
        <Actions>
          <Button type="submit" disabled={isLoading}>{isLoading ? 'Logging in...' : 'Log in'}</Button>
          <ChromeButton type="button" onClick={onToggleTheme}>{themeMode === 'light' ? 'Dark mode' : 'Light mode'}</ChromeButton>
        </Actions>
      </Card>
    </Shell>
  );
}

type MissingDirectoryProps = CommonProps & {
  onLogout: () => void;
};

export function MissingDirectoryScreen({ themeMode, onToggleTheme, onLogout }: MissingDirectoryProps) {
  return (
    <Shell>
      <Card as="div">
        <Title>Directory missing</Title>
        <Hint>Your PocketBase user record needs a <code>directory</code> value before BlanketChat can talk to opencode.</Hint>
        <Actions>
          <ChromeButton type="button" onClick={onToggleTheme}>{themeMode === 'light' ? 'Dark mode' : 'Light mode'}</ChromeButton>
          <DangerButton type="button" onClick={onLogout}>Logout</DangerButton>
        </Actions>
      </Card>
    </Shell>
  );
}
